/* eslint-disable jsx-a11y/label-has-associated-control */
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { Button } from '@/components/ui/catalyst/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { GroupedCombobox } from '@/components/ui/grouped-combobox';
import { cn } from '@/lib/utils';
import type { ReadTemplateField } from '@/services/api/generated/templatesV2Service';
import {
  useTemplateContextFieldsQuery,
  useTemplateFieldCreateMutation,
  useTemplateFieldUpdateMutation,
} from '@/services/api/templatesV2ServiceEnhanced';

// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------

const FIELD_TYPE_VALUES = ['TEXT', 'DATE', 'CHECKBOX', 'IMAGE', 'ARRAY', 'SIGNATURE', 'INITIALS'] as const;
const FIELD_SOURCE_VALUES = ['MANUAL', 'CONTEXT', 'SIGNING'] as const;

const templateFieldSchema = z
  .object({
    reference: z.string().min(1, 'Reference is required'),
    field_type: z.enum(FIELD_TYPE_VALUES, {
      required_error: 'Field type is required',
    }),
    source: z.enum(FIELD_SOURCE_VALUES, {
      required_error: 'Source is required',
    }),
    context_key: z.string().optional(),
    default_value: z.string().optional(),
    required: z.boolean().default(false),
    pdf_page_number: z.coerce.number().int().positive().optional().or(z.literal('')),
    pdf_position_x: z.coerce.number().nonnegative().optional().or(z.literal('')),
    pdf_position_y: z.coerce.number().nonnegative().optional().or(z.literal('')),
    pdf_position_width: z.coerce.number().nonnegative().optional().or(z.literal('')),
    pdf_position_height: z.coerce.number().nonnegative().optional().or(z.literal('')),
  })
  .refine(
    (data) => data.source !== 'CONTEXT' || (data.context_key && data.context_key.length > 0),
    {
      message: 'Context key is required when source is Context',
      path: ['context_key'],
    },
  );

type TemplateFieldFormData = z.infer<typeof templateFieldSchema>;

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const FIELD_TYPE_LABELS: Record<(typeof FIELD_TYPE_VALUES)[number], string> = {
  TEXT: 'Text',
  DATE: 'Date',
  CHECKBOX: 'Checkbox',
  IMAGE: 'Image',
  ARRAY: 'Array',
  SIGNATURE: 'Signature',
  INITIALS: 'Initials',
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface TemplateFieldEditorProps {
  open: boolean;
  onClose: () => void;
  templateId: string;
  templateFormat: string;
  field?: ReadTemplateField;
  onSuccess?: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const TemplateFieldEditor = ({
  open,
  onClose,
  templateId,
  templateFormat,
  field,
  onSuccess,
}: TemplateFieldEditorProps) => {
  const isEdit = Boolean(field);
  const isPdf = templateFormat === 'PDF';

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<TemplateFieldFormData>({
    resolver: zodResolver(templateFieldSchema),
    defaultValues: {
      reference: field?.reference ?? '',
      field_type: field?.field_type ?? undefined,
      source: field?.source ?? 'MANUAL',
      context_key: field?.context_key ?? '',
      default_value: field?.default_value ?? '',
      required: field?.required ?? false,
      pdf_page_number: field?.pdf_page_number ?? '',
      pdf_position_x: field?.pdf_position?.x ?? '',
      pdf_position_y: field?.pdf_position?.y ?? '',
      pdf_position_width: field?.pdf_position?.width ?? '',
      pdf_position_height: field?.pdf_position?.height ?? '',
    },
  });

  const [createField, { isLoading: isCreating }] = useTemplateFieldCreateMutation();
  const [updateField, { isLoading: isUpdating }] = useTemplateFieldUpdateMutation();
  const isSaving = isCreating || isUpdating;

  const source = watch('source');
  const contextKeyValue = watch('context_key');

  const { data: contextFields } = useTemplateContextFieldsQuery();

  const contextFieldGroups = useMemo(() => {
    if (!contextFields?.categories) return [];
    return contextFields.categories.map((cat) => ({
      label: cat.label,
      options: cat.fields.map((f) => ({
        id: f.key,
        name: f.label,
        fieldType: f.type,
      })),
    }));
  }, [contextFields]);

  const selectedContextField = useMemo(() => {
    if (!contextKeyValue) return null;
    return (
      contextFieldGroups.flatMap((g) => g.options).find((o) => o.id === contextKeyValue) ?? null
    );
  }, [contextKeyValue, contextFieldGroups]);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const onSubmit = useCallback(
    async (data: TemplateFieldFormData) => {
      const pdfPosition =
        data.pdf_position_x !== '' &&
        data.pdf_position_y !== '' &&
        data.pdf_position_width !== '' &&
        data.pdf_position_height !== ''
          ? {
              x: Number(data.pdf_position_x),
              y: Number(data.pdf_position_y),
              width: Number(data.pdf_position_width),
              height: Number(data.pdf_position_height),
            }
          : null;

      const body = {
        reference: data.reference,
        field_type: data.field_type,
        source: data.source,
        context_key: data.source === 'CONTEXT' ? data.context_key || null : null,
        default_value: data.default_value || null,
        required: data.required,
        pdf_page_number: data.pdf_page_number !== '' ? Number(data.pdf_page_number) : null,
        pdf_position: pdfPosition,
      };

      try {
        if (isEdit && field) {
          await updateField({
            templateId,
            fieldId: field.id as string,
            body,
          }).unwrap();
          toast.success(`Field "${data.reference}" updated`);
        } else {
          await createField({ templateId, body }).unwrap();
          toast.success(`Field "${data.reference}" created`);
        }
        onSuccess?.();
        handleClose();
      } catch {
        toast.error(`Failed to ${isEdit ? 'update' : 'create'} field`);
      }
    },
    [isEdit, field, templateId, createField, updateField, onSuccess, handleClose],
  );

  const inputClassName = (hasError?: boolean) =>
    cn(
      'w-full rounded-md border px-3 py-2 text-sm font-medium shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500',
      hasError ? 'border-red-500' : 'border-gray-300',
    );

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Field' : 'Add Field'}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Update the configuration for this template field.'
              : 'Define a new field for this template.'}
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-4 px-1"
          id="template-field-form"
        >
          {/* Reference */}
          <div className="space-y-1.5">
            <label htmlFor="reference" className="block text-sm font-medium text-gray-700">
              Reference <span className="text-red-500">*</span>
            </label>
            <input
              id="reference"
              type="text"
              {...register('reference')}
              className={inputClassName(!!errors.reference)}
              placeholder="e.g. clientName"
            />
            {errors.reference && (
              <p className="text-sm font-medium text-red-600">{errors.reference.message}</p>
            )}
          </div>

          {/* Field Type */}
          <div className="space-y-1.5">
            <label htmlFor="field_type" className="block text-sm font-medium text-gray-700">
              Field Type <span className="text-red-500">*</span>
            </label>
            <select
              id="field_type"
              {...register('field_type')}
              className={inputClassName(!!errors.field_type)}
              defaultValue=""
            >
              <option value="" disabled>
                Select a field type
              </option>
              {FIELD_TYPE_VALUES.map((val) => (
                <option key={val} value={val}>
                  {FIELD_TYPE_LABELS[val]}
                </option>
              ))}
            </select>
            {errors.field_type && (
              <p className="text-sm font-medium text-red-600">{errors.field_type.message}</p>
            )}
          </div>

          {/* Source */}
          <fieldset className="space-y-2">
            <legend className="block text-sm font-medium text-gray-700">
              Source <span className="text-red-500">*</span>
            </legend>
            <div className="flex gap-6">
              {FIELD_SOURCE_VALUES.map((val) => (
                <label
                  key={val}
                  className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer"
                >
                  <input
                    type="radio"
                    value={val}
                    {...register('source')}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                  />
                  {val === 'MANUAL' ? 'Manual' : val === 'CONTEXT' ? 'Context' : 'Signing'}
                </label>
              ))}
            </div>
            {errors.source && (
              <p className="text-sm font-medium text-red-600">{errors.source.message}</p>
            )}
          </fieldset>

          {/* Context Key -- visible only when source=CONTEXT */}
          {source === 'CONTEXT' && (
            <div className="space-y-1.5">
              <GroupedCombobox
                label={
                  <span className="text-sm font-medium text-gray-700">
                    Context Key <span className="text-red-500">*</span>
                  </span>
                }
                groups={contextFieldGroups}
                value={selectedContextField}
                onChange={(option) =>
                  setValue('context_key', option?.id ?? '', { shouldValidate: true })
                }
                placeholder="Select a context field..."
                emptyText="No matching fields."
              />
              {errors.context_key && (
                <p className="text-sm font-medium text-red-600">{errors.context_key.message}</p>
              )}
            </div>
          )}

          {/* Default Value */}
          <div className="space-y-1.5">
            <label htmlFor="default_value" className="block text-sm font-medium text-gray-700">
              Default Value
            </label>
            <input
              id="default_value"
              type="text"
              {...register('default_value')}
              className={inputClassName()}
              placeholder="Optional default value"
            />
          </div>

          {/* Required */}
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              {...register('required')}
              className="h-4 w-4 rounded text-blue-600 focus:ring-blue-500"
            />
            Required field
          </label>

          {/* PDF-specific fields */}
          {isPdf && (
            <>
              <div className="space-y-1.5">
                <label
                  htmlFor="pdf_page_number"
                  className="block text-sm font-medium text-gray-700"
                >
                  PDF Page Number
                </label>
                <input
                  id="pdf_page_number"
                  type="number"
                  {...register('pdf_page_number')}
                  className={inputClassName(!!errors.pdf_page_number)}
                  placeholder="e.g. 1"
                  min={1}
                />
                {errors.pdf_page_number && (
                  <p className="text-sm font-medium text-red-600">
                    {errors.pdf_page_number.message}
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <span className="block text-sm font-medium text-gray-700">PDF Position</span>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label
                      htmlFor="pdf_position_x"
                      className="block text-xs font-medium text-gray-500"
                    >
                      X
                    </label>
                    <input
                      id="pdf_position_x"
                      type="number"
                      {...register('pdf_position_x')}
                      className={inputClassName(!!errors.pdf_position_x)}
                      placeholder="0"
                      min={0}
                    />
                  </div>
                  <div className="space-y-1">
                    <label
                      htmlFor="pdf_position_y"
                      className="block text-xs font-medium text-gray-500"
                    >
                      Y
                    </label>
                    <input
                      id="pdf_position_y"
                      type="number"
                      {...register('pdf_position_y')}
                      className={inputClassName(!!errors.pdf_position_y)}
                      placeholder="0"
                      min={0}
                    />
                  </div>
                  <div className="space-y-1">
                    <label
                      htmlFor="pdf_position_width"
                      className="block text-xs font-medium text-gray-500"
                    >
                      Width
                    </label>
                    <input
                      id="pdf_position_width"
                      type="number"
                      {...register('pdf_position_width')}
                      className={inputClassName(!!errors.pdf_position_width)}
                      placeholder="0"
                      min={0}
                    />
                  </div>
                  <div className="space-y-1">
                    <label
                      htmlFor="pdf_position_height"
                      className="block text-xs font-medium text-gray-500"
                    >
                      Height
                    </label>
                    <input
                      id="pdf_position_height"
                      type="number"
                      {...register('pdf_position_height')}
                      className={inputClassName(!!errors.pdf_position_height)}
                      placeholder="0"
                      min={0}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </form>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button outline type="button" onClick={handleClose}>
            Cancel
          </Button>
          <div className="flex-1" />
          <Button color="blue" type="submit" form="template-field-form" disabled={isSaving}>
            {isSaving && 'Saving...'}
            {!isSaving && isEdit && 'Save Changes'}
            {!isSaving && !isEdit && 'Add Field'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
